import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { ImportedCompanyData } from "../utils/companyUtils";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (companies: ImportedCompanyData[]) => Promise<void>;
  darkMode?: boolean;
  language?: 'en' | 'de';
}

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onClose,
  onImport,
  darkMode = true,
  language = 'en',
}) => {
  // Translations
  const translations = {
    en: {
      importCompanies: "Import Companies",
      uploadExcel: "Upload Excel (.xlsx) with columns:",
      name: "name",
      address: "address",
      topics: "topics",
      membership: "membership",
      displayed: "displayed",
      clickToSelect: "Click to select or drag and drop Excel file",
      dropFileHere: "Drop file here",
      supportedFormats: "Supported formats: .xlsx and .xls",
      processingFile: "Processing file...",
      startingImport: "Starting import...",
      processingCompanyData: "Processing company data...",
      importCompleted: "Import completed successfully!",
      importFailed: "Import failed!",
      successfullyRead: "Successfully read",
      companies: "companies",
      previewData: "Preview data (display first 5 companies):",
      andOther: "and",
      otherCompanies: "other companies",
      cancel: "Cancel",
      importing: "Importing...",
      import: "Import",
      selectFile: "Select File",
      pleaseDropValid: "Please drop a valid Excel file (.xlsx or .xls)",
      couldNotFind: 'Could not find "name" and "address" columns in Excel file',
      noCompanyData: "No company data found in file",
      errorReading: "Error reading Excel file",
      errorImporting: "Error importing data",
    },
    de: {
      importCompanies: "Unternehmen importieren",
      uploadExcel: "Excel (.xlsx) hochladen mit Spalten:",
      name: "name",
      address: "adresse",
      topics: "themen",
      membership: "mitgliedschaft",
      displayed: "angezeigt",
      clickToSelect: "Klicken Sie, um eine Datei auszuwählen oder Excel-Datei per Drag & Drop",
      dropFileHere: "Datei hier ablegen",
      supportedFormats: "Unterstützte Formate: .xlsx und .xls",
      processingFile: "Datei wird verarbeitet...",
      startingImport: "Import wird gestartet...",
      processingCompanyData: "Unternehmensdaten werden verarbeitet...",
      importCompleted: "Import erfolgreich abgeschlossen!",
      importFailed: "Import fehlgeschlagen!",
      successfullyRead: "Erfolgreich gelesen",
      companies: "Unternehmen",
      previewData: "Vorschau (erste 5 Unternehmen anzeigen):",
      andOther: "und",
      otherCompanies: "weitere Unternehmen",
      cancel: "Abbrechen",
      importing: "Wird importiert...",
      import: "Importieren",
      selectFile: "Datei auswählen",
      pleaseDropValid: "Bitte eine gültige Excel-Datei (.xlsx oder .xls) ablegen",
      couldNotFind: 'Spalten "name" und "adresse" in Excel-Datei nicht gefunden',
      noCompanyData: "Keine Unternehmensdaten in Datei gefunden",
      errorReading: "Fehler beim Lesen der Excel-Datei",
      errorImporting: "Fehler beim Importieren der Daten",
    },
  };

  const t = translations[language];
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ImportedCompanyData[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ImportedCompanyData[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      parseExcelFile(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFile(droppedFile);
      setError(null);
      parseExcelFile(droppedFile);
    } else {
      setError(t.pleaseDropValid);
    }
  };

  const parseExcelFile = async (file: File) => {
    setIsProcessing(true);
    setError(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      console.log("Raw Excel data:", jsonData);
      console.log("First few rows:", jsonData.slice(0, 3));

      // Find header row
      let headerRowIndex = -1;
      let nameColIndex = -1;
      let addressColIndex = -1;
      let topicsColIndex = -1;
      let membershipColIndex = -1;
      let displayedColIndex = -1;

      const normalizeHeader = (cell: string) =>
        cell.replace(/\*+/g, "").toLowerCase().trim();

      const findColumnIndex = (row: string[], keywords: string[]) =>
        row.findIndex(
          (cell) =>
            cell &&
            typeof cell === "string" &&
            keywords.some((keyword) =>
              normalizeHeader(cell).includes(keyword)
            )
        );

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as string[];
        if (row && row.length > 0) {
          console.log(`Row ${i}:`, row);

          const nameIndex = findColumnIndex(row, ["name"]);
          const addressIndex = findColumnIndex(row, ["address"]);
          const topicsIndex = findColumnIndex(row, ["topic"]);
          const membershipIndex = findColumnIndex(row, ["membership"]);
          const displayedIndex = findColumnIndex(row, ["displayed", "show"]);

          console.log(
            `Row ${i} - nameIndex: ${nameIndex}, addressIndex: ${addressIndex}`
          );

          if (nameIndex !== -1 && addressIndex !== -1) {
            headerRowIndex = i;
            nameColIndex = nameIndex;
            addressColIndex = addressIndex;
            topicsColIndex = topicsIndex;
            membershipColIndex = membershipIndex;
            displayedColIndex = displayedIndex;
            console.log(
              `Found header at row ${i}, nameCol: ${nameColIndex}, addressCol: ${addressColIndex}, topicsCol: ${topicsColIndex}, membershipCol: ${membershipColIndex}, displayedCol: ${displayedColIndex}`
            );
            break;
          }
        }
      }

      if (headerRowIndex === -1) {
        throw new globalThis.Error(t.couldNotFind);
      }

      // Parse data rows
      const companies: ImportedCompanyData[] = [];
      for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
        const row = jsonData[i] as string[];
        if (row && row[nameColIndex] && row[addressColIndex]) {
          const topicsValue =
            topicsColIndex !== -1 && row[topicsColIndex]
              ? String(row[topicsColIndex]).trim()
              : undefined;
          const rawMembership =
            membershipColIndex !== -1 && row[membershipColIndex]
              ? String(row[membershipColIndex]).trim()
              : undefined;
          const membershipValue =
            rawMembership &&
            ["personal", "apm", "rpm"].some((keyword) =>
              rawMembership.toLowerCase().includes(keyword)
            )
              ? undefined
              : rawMembership;
          const displayedValue =
            displayedColIndex !== -1 && row[displayedColIndex]
              ? String(row[displayedColIndex]).trim()
              : undefined;

          companies.push({
            name: String(row[nameColIndex]).trim(),
            address: String(row[addressColIndex]).trim(),
            topics: topicsValue,
            membership: membershipValue,
            displayed: displayedValue,
          });
        }
      }

      if (companies.length === 0) {
        throw new globalThis.Error(t.noCompanyData);
      }

      setParsedData(companies);
      setPreviewData(companies.slice(0, 5)); // Show first 5 companies as preview
    } catch (err) {
      setError(
        err instanceof globalThis.Error ? err.message : t.errorReading
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    // If no file selected, open file picker
    if (!file || parsedData.length === 0) {
      fileInputRef.current?.click();
      return;
    }

    // If file is selected and parsed, proceed with import
    if (parsedData.length > 0) {
      setIsImporting(true);
      setError(null);
      setImportProgress(0);
        setImportStatus(t.startingImport);

      let progressInterval: NodeJS.Timeout | null = null;

      try {
        // Simulate progress that matches actual import time (synchronized)
        const totalSteps = 20; // Total progress steps
        const stepInterval = 150; // Update every 150ms
        let currentStep = 0;

        progressInterval = setInterval(() => {
          currentStep++;
          const progress = Math.min((currentStep / totalSteps) * 99, 99); // Stop at 99%
          setImportProgress(progress);

          if (currentStep >= totalSteps) {
            if (progressInterval) {
              clearInterval(progressInterval);
            }
          }
        }, stepInterval);

        setImportStatus(t.processingCompanyData);

        await onImport(parsedData);

        if (progressInterval) {
          clearInterval(progressInterval);
        }
        setImportProgress(100);
        setImportStatus(t.importCompleted);

        // Close dialog immediately after import
        handleClose();
      } catch (error) {
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        setError(
          error instanceof Error ? error.message : t.errorImporting
        );
        setImportStatus(t.importFailed);
      } finally {
        setIsImporting(false);
      }
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setPreviewData([]);
    setError(null);
    setIsProcessing(false);
    setIsImporting(false);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const getFileIcon = () => {
    if (isProcessing) return <LinearProgress />;
    if (error) return <ErrorIcon color="error" />;
    if (parsedData.length > 0) return <CheckCircle color="success" />;
    return <CloudUpload color="primary" />;
  };

  const primaryTextColor = darkMode ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.87)";
  const secondaryTextColor = darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)";
  const lightTextColor = darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)";

  const dialogStyles = {
    "& .MuiPaper-root": {
      background: darkMode
        ? isDragging
          ? "linear-gradient(145deg, #0D1234, #1a2a4a)"
          : "linear-gradient(145deg, #0D1234, #050714)"
        : isDragging
        ? "linear-gradient(145deg, #F5F5F5, #E0E0E0)"
        : "linear-gradient(145deg, #FFFFFF, #F5F5F5)",
      color: primaryTextColor,
      borderRadius: 3,
      border: isDragging
        ? "2px dashed #34A936"
        : darkMode
        ? "1px solid rgba(200, 162, 78, 0.6)"
        : "1px solid rgba(0, 0, 0, 0.2)",
      boxShadow: darkMode
        ? "0 25px 60px rgba(0, 0, 0, 0.65)"
        : "0 8px 24px rgba(0, 0, 0, 0.15)",
      transition: "all 0.2s ease",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={dialogStyles}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DialogTitle
        sx={{
          borderBottom: darkMode
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
          mb: 1,
          color: primaryTextColor,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CloudUpload sx={{ color: "#34A936" }} />
          <Typography sx={{ color: primaryTextColor }}>{t.importCompanies}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ color: secondaryTextColor }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: secondaryTextColor }}
          >
            {t.uploadExcel} <strong>{t.name}</strong>,{" "}
            <strong>{t.address}</strong>, {language === 'en' ? 'and optional' : 'und optional'} <strong>{t.topics}</strong>,{" "}
            <strong>{t.membership}</strong>, <strong>{t.displayed}</strong> ({language === 'en' ? '"YES"/"NO"' : '"JA"/"NEIN"'})
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${isDragging ? "#34A936" : darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"}`,
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: isDragging
                ? "rgba(52,169,54,0.2)"
                : darkMode
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
              "&:hover": {
                borderColor: "#34A936",
                backgroundColor: "rgba(52,169,54,0.1)",
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            {getFileIcon()}

            <Typography
              variant="h6"
              sx={{ mt: 1, mb: 1, color: primaryTextColor }}
            >
              {file
                ? file.name
                : isDragging
                ? t.dropFileHere
                : t.clickToSelect}
            </Typography>

            <Typography variant="body2" sx={{ color: lightTextColor }}>
              {t.supportedFormats}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ 
              mb: 2, 
              backgroundColor: darkMode ? "rgba(229,57,53,0.15)" : "rgba(229,57,53,0.1)", 
              color: darkMode ? "#fff" : "#d32f2f" 
            }}
          >
            {error}
          </Alert>
        )}

        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, color: secondaryTextColor }}
            >
              {t.processingFile}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {isImporting && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: "bold", color: "#34A936" }}
            >
              {importStatus}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: "100%" }}>
                <LinearProgress
                  variant="determinate"
                  value={importProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(0,0,0,0.1)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ minWidth: 40, color: secondaryTextColor }}
              >
                {Math.round(importProgress)}%
              </Typography>
            </Box>
          </Box>
        )}

        {parsedData.length > 0 && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <CheckCircle sx={{ color: "#34A936" }} />
              <Typography variant="h6" sx={{ color: primaryTextColor }}>
                {t.successfullyRead} {parsedData.length} {t.companies}
              </Typography>
            </Box>

            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: secondaryTextColor }}
            >
              {t.previewData}
            </Typography>

            <Box
              sx={{
                maxHeight: 240,
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: 6,
                },
                "&::-webkit-scrollbar-track": {
                  background: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(52,169,54,0.7)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#34A936",
                },
              }}
            >
              <List dense>
                {previewData.map((company, index) => (
                  <ListItem 
                    key={index} 
                    divider
                    sx={{
                      borderColor: darkMode 
                        ? "rgba(255,255,255,0.1)" 
                        : "rgba(0,0,0,0.12)"
                    }}
                  >
                    <ListItemIcon>
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(52,169,54,0.15)",
                          color: "#34A936",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={company.name}
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{ color: secondaryTextColor }}
                        >
                          {company.address}
                        </Typography>
                      }
                      primaryTypographyProps={{
                        sx: { color: primaryTextColor },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {parsedData.length > 5 && (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: secondaryTextColor }}
              >
                ... {t.andOther} {parsedData.length - 5} {t.otherCompanies}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: darkMode
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isImporting}
          sx={{
            color: "#34A936",
            "&:hover:not(:disabled)": { 
              backgroundColor: darkMode
                ? "rgba(52,169,54,0.08)"
                : "rgba(52,169,54,0.05)"
            },
            "&:disabled": {
              color: "rgba(52, 169, 54, 0.5) !important",
              cursor: "not-allowed",
            },
            "&.Mui-disabled": {
              color: "rgba(52, 169, 54, 0.5) !important",
            },
          }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={isProcessing || isImporting}
          sx={{
            backgroundColor: parsedData.length === 0 ? "#4a4d63" : "#34A936",
            color: "#fff",
            "&:hover:not(:disabled)": {
              backgroundColor:
                parsedData.length === 0 ? "#4a4d63" : "#2A8A2B",
            },
            "&:disabled": {
              backgroundColor: "#4a4d63 !important",
              color: "rgba(255, 255, 255, 0.5) !important",
              cursor: "not-allowed",
            },
            "&.Mui-disabled": {
              backgroundColor: "#4a4d63 !important",
              color: "rgba(255, 255, 255, 0.5) !important",
            },
            "&.MuiButton-contained.Mui-disabled": {
              backgroundColor: "#4a4d63 !important",
              color: "rgba(255, 255, 255, 0.5) !important",
            },
          }}
          startIcon={
            isImporting ? (
              <LinearProgress sx={{ width: 20, height: 20 }} />
            ) : (
              <CloudUpload />
            )
          }
        >
          {isImporting
            ? t.importing
            : parsedData.length > 0
            ? `${t.import} (${parsedData.length} ${t.companies})`
            : t.selectFile}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
